import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  TablePagination,
  TextField,
} from "@mui/material";
import {
  useEffect,
  useState,
} from "react";
import CustomTable from "components/custom-table/Table";
import { formatDate } from "utils/formatDate";
import DocumentStatus from "./DocumentStatus";
import CompanyProfileReviewModal from "./CompanyProfileReviewModal";
import {
  workflowCompanies,
  workflowReviewQueue,
} from "../service/WorkflowService";
import {
  getCurrentDocumentAccess,
} from "../service/DocumentAccessService";
import { useCompanyDocumentsLanguage } from "../i18n/CompanyDocumentsLanguageContext";

const dateOnly = (value) => {
  if (!value) return "—";

  return (
    String(value).match(
      /^(\d{4}-\d{2}-\d{2})/,
    )?.[1] || "—"
  );
};

export default function WorkflowReviewQueue() {
  const { t } = useCompanyDocumentsLanguage();

  const [filters, setFilters] =
    useState({
      status: "SUBMITTED",
      company_id: "",
    });

  const [page, setPage] =
    useState(0);

  const [companies, setCompanies] =
    useState([]);

  const [rows, setRows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [selected, setSelected] =
    useState(null);

  const [canReview, setCanReview] =
    useState(false);

  const [refresh, setRefresh] =
    useState(0);

  useEffect(() => {
    let active = true;

    Promise.all([
      workflowCompanies(),
      getCurrentDocumentAccess(),
    ])
      .then(
        ([
          companyRows,
          access,
        ]) => {
          if (!active) return;

          setCompanies(
            companyRows || [],
          );

          setCanReview(
            !!access?.review,
          );
        },
      )
      .catch((e) => {
        if (active) {
          setError(
            e.message,
          );
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError("");

    workflowReviewQueue(
      "NAME",
      filters.status,
      filters.company_id,
    )
      .then((value) => {
        if (active) {
          setRows(
            value || [],
          );
        }
      })
      .catch((e) => {
        if (active) {
          setRows([]);
          setError(
            e.message,
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    filters,
    refresh,
  ]);

  const filter = (
    key,
    value,
  ) => {
    setPage(0);

    setFilters({
      ...filters,
      [key]: value,
    });

    setSelected(null);
  };

  const visibleRows =
    rows.slice(
      page * 20,
      page * 20 + 20,
    );

  return (
    <div className="bg-white shadow-sm rounded-lg p-5 grid gap-4 min-w-0">
      <div className="flex justify-between">
        <div>
          <h1 className="text-lg font-bold">
            {t("adminReview.profile.title")}
          </h1>

          <p className="text-sm text-[#5F737F]">
            {t("adminReview.profile.description")}
          </p>
        </div>

        <Button
          onClick={() =>
            setRefresh(
              (value) =>
                value + 1,
            )
          }
        >
          {t("adminReview.common.refresh")}
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Autocomplete
          size="small"
          options={companies}
          value={
            companies.find(
              (company) =>
                company.id ===
                filters.company_id,
            ) || null
          }
          getOptionLabel={(
            company,
          ) =>
            `${company.name} (${
              company.effect_code ||
              company.id
            })`
          }
          isOptionEqualToValue={(
            a,
            b,
          ) =>
            a.id === b.id
          }
          onChange={(
            _,
            company,
          ) =>
            filter(
              "company_id",
              company?.id ||
                "",
            )
          }
          renderInput={(
            params,
          ) => (
            <TextField
              {...params}
              label={t("adminReview.common.issuer")}
              placeholder={t("adminReview.common.allIssuers")}
            />
          )}
        />

        <TextField
          select
          size="small"
          label={t("adminReview.common.status")}
          value={
            filters.status
          }
          onChange={(e) =>
            filter(
              "status",
              e.target.value,
            )
          }
        >
          {[
            [
              "ALL",
              t("adminReview.common.all"),
            ],
            [
              "SUBMITTED",
              t("adminReview.common.submitted"),
            ],
            [
              "APPROVED",
              t("adminReview.common.approved"),
            ],
            [
              "REJECTED",
              t("adminReview.common.rejected"),
            ],
          ].map(
            ([
              value,
              label,
            ]) => (
              <MenuItem
                key={value}
                value={value}
              >
                {label}
              </MenuItem>
            ),
          )}
        </TextField>
      </div>

      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress
          size={24}
        />
      ) : (
        <CustomTable
          id="company-profile-review-list"
          showPagination={
            false
          }
          headCells={[
            {
              id: "issuer",
              label: t("adminReview.common.issuer"),
            },
            {
              id: "change",
              label:
                t("adminReview.profile.nameChange"),
            },
            {
              id: "requester",
              label: t("adminReview.profile.requester"),
            },
            {
              id: "submitted",
              label: t("adminReview.profile.submitted"),
            },
            {
              id: "status",
              label: t("adminReview.common.status"),
            },
            {
              id: "action",
              label: t("adminReview.common.action"),
            },
          ]}
          bodyCells={visibleRows.map(
            (row) => ({
              id: row.id,

              issuer: (
                <div>
                  <p>
                    {
                      row.company_name
                    }
                  </p>

                  <p className="text-xs text-[#5F737F]">
                    {row.company_code ||
                      row.company_id}
                  </p>
                </div>
              ),

              change: (
                <div>
                  <p>
                    {row.old_name ||
                      "—"}
                  </p>

                  <p className="font-medium">
                    →{" "}
                    {row.proposed_name ||
                      "—"}
                  </p>

                  <p className="text-xs text-[#5F737F] mt-1">
                    {t("adminReview.profile.effective")}{" "}
                    {dateOnly(
                      row.effective_date,
                    )}
                  </p>
                </div>
              ),

              requester:
                row.requester_name ||
                t("adminReview.common.user", {
                  id: row.requested_by,
                }),

              submitted: (
                <div>
                  <p>
                    {formatDate.toFullDateTime(
                      row.submitted_at,
                    )}
                  </p>
                </div>
              ),

              status: (
                <DocumentStatus
                  status={
                    row.status
                  }
                />
              ),

              action: (
                <Button
                  onClick={() =>
                    setSelected(
                      row,
                    )
                  }
                >
                  {t("adminReview.common.review")}
                </Button>
              ),
            }),
          )}
        />
      )}

      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(
          _,
          value,
        ) =>
          setPage(value)
        }
        rowsPerPage={20}
        rowsPerPageOptions={[
          20,
        ]}
      />

      {selected && (
        <CompanyProfileReviewModal
          record={selected}
          canReview={
            canReview
          }
          onClose={() =>
            setSelected(null)
          }
          onSaved={() => {
            setSelected(null);
            setPage(0);
            setRefresh(
              (value) =>
                value + 1,
            );
          }}
        />
      )}
    </div>
  );
}
